import { useSocketList } from 'src/utils/socket';
import React, { useCallback, useEffect, useState } from 'react';
import MUIDataTable, {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  MUIDataTableState,
  MUISortOptions,
} from 'mui-datatables';
import { useHistory, useLocation } from 'react-router';
import SentinelLogo from '../../assets/images/SentinelLogo.png';
import { SocketContextPortal } from '../../utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { DqLoading } from '../DqLoading';
import { useIntl, FormattedMessage } from 'react-intl';
import { GenericMessages } from '../../intl/GenericMessages';
import { IonButton } from '@ionic/react';
import { Firmware, SearchFirmwareRequest } from 'src/models/firmware';
import { CreateOrUpdateFirmwareModal } from './CreateOrUpdateFirmwareModal';
import { FirmwareMessages } from 'src/intl/FirmwareMessages';
import { addFirmware } from 'src/utils/api/createFirmware';
import { FirmwareDetailsData, FirmwareFile } from './types';
import { DqText } from '../dq-text';
import { ListFirmwareTiersRequest, useGetFirmwareTiersQuery } from '../../redux/api/firmwareTiers';
import { useSocketSearch } from '../../utils/socket/useSocketSearch';
import { ListOrganizationsRequest } from '../../models/organization';
import { useToast } from '../reusable-components/useToast';

interface FirmwareGridItem {
  _id: string;
  version: string;
  displayName: string;
  releaseType: string;
  isHidden: string; // Display is 'Archived' or 'Active'
  fileName: string;
}

// Route based actions that can be performed on the page.
export enum FirmwareStatusActions {
  'CreateSuccessful',
  'DeleteSuccessful',
}

const FirmwareGrid: React.FC = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { state: locationState } = useLocation<{ action?: FirmwareStatusActions }>();
  const [createFirmwareModalOpen, setCreateFirmwareModalOpen] = useState(false);
  const [columns, setColumns] = useState<MUIDataTableColumnDef[]>([]);
  const [sortOrder, setSortOrder] = useState<MUISortOptions>({
    name: 'createdAt',
    direction: 'desc',
  });
  const [present] = useToast();
  const [fwListOptions] = useState<SearchFirmwareRequest>({
    page: 0,
    pageSize: 10000,
    sortOrder: 'desc',
    sortProperty: 'createdAt',
  });
  const { items: firmwares, fetched: firmwareFetched } = useSocketSearch(
    SocketContextSentinel,
    'firmware',
    fwListOptions,
  );

  const [orgRequest] = useState<ListOrganizationsRequest>({
    pageNumber: 0,
    pageSize: 10000,
    statuses: ['active'],
    sort: { order: 'asc', property: 'orgName' },
  });
  const [firmwareTierRequest] = useState<ListFirmwareTiersRequest>({
    pageNumber: 0,
    pageSize: 10000,
    sort: { order: 'asc', property: 'tierName' },
  });
  const { items: organizations } = useSocketList(SocketContextPortal, 'organizations', orgRequest, 'listV2');
  const { data: firmwareTiers } = useGetFirmwareTiersQuery(firmwareTierRequest, {
    refetchOnMountOrArgChange: true,
  });

  const handleFirmwareDetailsSubmit = async (
    formikValues: FirmwareDetailsData,
    firmwareFile: FirmwareFile | undefined,
  ) => {
    setCreateFirmwareModalOpen(false);
    const deploymentTypeValue = formikValues.deploymentType;
    if (!firmwareFile) return alert('no file!');
    const fileParts = firmwareFile.name.split('.');
    const requestData: Record<string, any> = {
      releaseType: formikValues.deploymentType,
      binFile: formikValues.binFile,
      isHidden: formikValues.archiveFirmware,
      displayName: formikValues.displayName,
      version: formikValues.fwVersion,
      fileName: fileParts[0],
      fileType: fileParts[1],
      firmwareTierId: formikValues.firmwareTier,
    };
    if (deploymentTypeValue === 'beta') {
      requestData.beta = formikValues.users;
    }
    if (deploymentTypeValue === 'custom') {
      requestData.custom = formikValues.organization;
    }
    if (deploymentTypeValue === 'test') {
      requestData.users = formikValues.users;
      requestData.orgs = formikValues.organizations;
    }
    addFirmware(firmwareFile, requestData)
      .then(data => {
        history.replace(`/dev-firmware/${data.data._id}`, { action: FirmwareStatusActions.CreateSuccessful });
      })
      .catch(e => {
        console.log('Firmware not created!', e);
        present(
          {
            message: formatMessage(FirmwareMessages.firmwareCreationFailure),
          },
          'danger',
        );
      });
  };

  // Show deleted successful toast if the action occurred.
  useEffect(() => {
    if (locationState?.action === FirmwareStatusActions.DeleteSuccessful) {
      history.replace({ state: {} });
      present(
        {
          message: formatMessage(FirmwareMessages.firmwareDeletionSuccessful),
        },
        'success',
      );
    }
  }, [formatMessage, history, locationState, present]);

  const gridItems = React.useMemo(() => {
    return firmwares.results.map(fw => {
      const result: FirmwareGridItem = {
        _id: fw._id,
        version: fw.version,
        displayName: fw.displayName,
        releaseType: fw.releaseType,
        isHidden:
          fw.isHidden === 'true' ? formatMessage(FirmwareMessages.archived) : formatMessage(FirmwareMessages.active),
        fileName: fw.fileName,
      };
      return result;
    });
  }, [firmwares, formatMessage]);

  const getFileName = (row: Firmware) => {
    return <DqText>{`${row?.fileName}.${row?.fileType}`}</DqText>;
  };

  const getType = useCallback(
    (row: Firmware) => {
      switch (row?.releaseType) {
        case 'global':
          return formatMessage(FirmwareMessages.global);
        case 'beta':
          return formatMessage(FirmwareMessages.beta);
        case 'dev':
          return formatMessage(FirmwareMessages.dev);
        case 'custom':
          return formatMessage(FirmwareMessages.custom);
        case 'test':
          return formatMessage(FirmwareMessages.fieldTest);
        case 'paid':
          const firmwareTierName = firmwareTiers?.find(tier => tier._id === row.firmwareTierId)?.tierName;
          return firmwareTierName
            ? `${formatMessage(FirmwareMessages.paid)} - ${firmwareTierName}`
            : formatMessage(FirmwareMessages.paid);
        default:
          return formatMessage(FirmwareMessages.unknown);
      }
    },
    [firmwareTiers, formatMessage],
  );

  const handleTableChange = (eventType: string, tableState: MUIDataTableState) => {
    switch (eventType) {
      case 'viewColumnsChange':
        // It will only accept data like this, but it sends it back flattened
        const tempColumns = tableState.columns.map(column => ({ ...column, options: { ...column } }));
        setColumns(tempColumns);
        break;
      case 'sort':
        setSortOrder(tableState.sortOrder);
        break;
    }
  };

  const handleCreate = () => {
    setCreateFirmwareModalOpen(true);
  };

  const handleView = (data: any) => {
    history.push(`/dev-firmware/${data[0]}`);
  };

  useEffect(() => {
    setColumns([
      {
        label: 'Id',
        name: '_id', // must be column 0! That way it is data[0] on rowClick. Thanks.
        options: {
          display: false,
          filter: false,
        },
      },
      {
        label: 'Version',
        name: 'version',
        options: {
          filter: false,
        },
      },
      {
        label: 'Display Name',
        name: 'displayName',
        options: {
          filter: false,
        },
      },
      {
        label: 'Type',
        name: 'releaseType',
        options: {
          filter: true,

          customBodyRenderLite: dataIndex => getType(firmwares.results[dataIndex]),
        },
      },
      {
        label: 'Status',
        name: 'isHidden',
        options: {
          filter: false,
        },
      },
      {
        label: 'File',
        name: 'file_name',
        options: {
          filter: false,

          customBodyRenderLite: dataIndex => getFileName(firmwares.results[dataIndex]),
        },
      },
    ]);
  }, [firmwares, formatMessage, getType]);

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    onRowClick: handleView,
    onTableChange: (eventType: string, tableState: MUIDataTableState) => handleTableChange(eventType, tableState),
    customToolbar: () => (
      <IonButton onClick={() => handleCreate()} color="primary">
        {formatMessage(FirmwareMessages.create)}
      </IonButton>
    ),
    rowsPerPageOptions: [10, 25, 50],
    print: false,
    download: false,
    sortOrder: sortOrder,
    textLabels: {
      body: {
        noMatch: !firmwareFetched ? (
          <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <DqLoading width="100px" img={SentinelLogo} />
          </div>
        ) : (
          <FormattedMessage {...FirmwareMessages.noFirmwaresMatchSearch} />
        ),
        toolTip: formatMessage(GenericMessages.dataTablesSort),
      },
      pagination: {
        next: formatMessage(GenericMessages.dataTablesNextPage),
        previous: formatMessage(GenericMessages.dataTablesPreviousPage),
        rowsPerPage: formatMessage(GenericMessages.dataTablesRowsPerPage),
        displayRows: formatMessage(GenericMessages.of),
      },
      toolbar: {
        search: formatMessage(GenericMessages.searchPlaceholder),
        viewColumns: formatMessage(GenericMessages.dataTablesViewColumns),
        filterTable: formatMessage(GenericMessages.dataTablesFilterTable),
      },
      filter: {
        all: formatMessage(GenericMessages.dataTablesAll),
        title: formatMessage(GenericMessages.dataTablesFilters),
        reset: formatMessage(GenericMessages.dataTablesReset),
      },
      viewColumns: {
        title: formatMessage(GenericMessages.dataTablesShowColumns),
      },
    },
  } as MUIDataTableOptions;

  return (
    <>
      <MUIDataTable title="Firmware Table" columns={columns} data={gridItems} options={options} />
      <CreateOrUpdateFirmwareModal
        isOpen={createFirmwareModalOpen}
        onClose={() => setCreateFirmwareModalOpen(false)}
        organizations={organizations}
        firmwareTiers={firmwareTiers ?? []}
        onSave={(formikValues: FirmwareDetailsData, firmwareFile: FirmwareFile | undefined) =>
          handleFirmwareDetailsSubmit(formikValues, firmwareFile)
        }
      />
    </>
  );
};

export default FirmwareGrid;
